<template>
  <div class="bp-station-bar">
    <!-- 左：油站名称（集团站可点击展开搜索下拉） -->
    <el-popover
      v-if="selectable"
      v-model="popoverVisible"
      placement="bottom-start"
      trigger="click"
      :visible-arrow="false"
      popper-class="bp-station-popover"
      :width="260"
    >
      <div class="bp-station-dropdown">
        <div class="search-box">
          <input
            v-model="keyword"
            class="search-input"
            type="text"
            placeholder="搜索油站"
          />
        </div>
        <div class="station-list">
          <div
            v-for="item in filteredStations"
            :key="item.gasId"
            class="station-item"
            :class="{ active: item.gasId === value }"
            @click="onSelect(item)"
          >
            <!-- eslint-disable-next-line vue/no-v-html -->
            <span class="name" v-html="highlight(item.gasName)"></span>
            <img v-if="item.gasId === value" class="check" :src="checkIcon" alt="" />
          </div>
          <div v-if="!filteredStations.length" class="empty">无匹配油站</div>
        </div>
      </div>
      <div slot="reference" class="station-name clickable">
        <span class="bar"></span>
        <span class="name">{{ currentName }}</span>
        <span
          class="arrow"
          :class="{ up: popoverVisible }"
          :style="{ '--arrow-icon': `url(${triangleIcon})` }"
        ></span>
      </div>
    </el-popover>

    <!-- 单站：仅展示名称 -->
    <div v-else class="station-name">
      <span class="bar"></span>
      <span class="name">{{ currentName }}</span>
    </div>

    <!-- 右：更新时间 -->
    <div v-if="updateTime" class="update-time">
      <img class="time-icon" :src="timeIcon" alt="" />
      <span>更新于{{ updateTime }}</span>
    </div>
  </div>
</template>

<script>
import timeIcon from '../assets/icon-time.svg'
import triangleIcon from '../assets/icon-triangle-down.svg'
import checkIcon from '../assets/icon-check.svg'

export default {
  name: 'BpStationBar',
  props: {
    // 当前选中 gasId
    value: { type: [String, Number], default: '' },
    // 油站列表 [{ gasId, gasName }]
    stations: { type: Array, default: () => [] },
    // 当前油站名（单站时直接传名称）
    currentName: { type: String, default: '' },
    // 是否可选（集团站 true）
    selectable: { type: Boolean, default: false },
    // 更新时间文案
    updateTime: { type: String, default: '' }
  },
  data () {
    return {
      timeIcon,
      triangleIcon,
      checkIcon,
      popoverVisible: false,
      keyword: ''
    }
  },
  computed: {
    filteredStations () {
      const kw = this.keyword.trim()
      if (!kw) return this.stations
      return this.stations.filter(s => s.gasName.indexOf(kw) > -1)
    }
  },
  methods: {
    onSelect (item) {
      this.popoverVisible = false
      this.keyword = ''
      if (item.gasId !== this.value) this.$emit('change', item)
    },
    // 关键词高亮（蓝色）
    highlight (name) {
      const kw = this.keyword.trim()
      if (!kw) return this.escape(name)
      const safeName = this.escape(name)
      const safeKw = this.escape(kw).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      return safeName.replace(new RegExp(safeKw, 'g'), m => `<span class="hl">${m}</span>`)
    },
    escape (str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
    }
  }
}
</script>

<style lang="less" scoped>
@import '../styles.less';
.bp-station-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 60px;
  padding: 0 20px;
  background: @bp-card-bg;
  border-radius: @bp-card-radius;
  .station-name {
    display: flex;
    align-items: center;
    &.clickable { cursor: pointer; }
    .bar {
      width: 6px;
      height: 18px;
      border-radius: 6px;
      background: @bp-bar-gradient;
      margin-right: 8px;
    }
    .name {
      font-family: @font-cn;
      font-weight: 600;
      font-size: 20px;
      line-height: 20px;
      color: @bp-title;
    }
    .arrow {
      width: 10px;
      height: 7px;
      margin-left: 8px;
      // 收起态浅蓝、展开态主蓝（按 Figma B 端 / 搜索下拉框两态）
      background-color: @bp-grid;
      -webkit-mask: var(--arrow-icon) no-repeat center / contain;
      mask: var(--arrow-icon) no-repeat center / contain;
      transition: transform 0.2s;
      &.up {
        transform: rotate(180deg);
        background-color: #3A89FF;
      }
    }
  }
  .update-time {
    display: flex;
    align-items: center;
    font-family: @font-cn;
    font-size: 14px;
    color: @bp-label;
    .time-icon {
      width: 14px;
      height: 14px;
      margin-right: 5px;
    }
  }
}
</style>

<style lang="less">
// popover 内容样式（非 scoped，popper 挂载在 body）
@import '../styles.less';
.bp-station-popover.el-popover {
  padding: 12px;
  border: 1px solid @bp-blue;
  border-radius: 10px;
  .bp-station-dropdown {
    .search-box {
      .search-input {
        width: 100%;
        height: 30px;
        box-sizing: border-box;
        padding: 5px 10px;
        background: @bp-subcard-bg;
        border: none;
        border-radius: 6px;
        font-size: 12px;
        color: #313948;
        outline: none;
        &::placeholder { color: @bp-label; }
      }
    }
    .station-list {
      margin-top: 10px;
      max-height: 240px;
      overflow-y: auto;
      .station-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 8px 10px;
        font-size: 12px;
        line-height: 20px;
        color: #313948;
        cursor: pointer;
        border-top: 1px solid @bp-divider;
        &:hover { background: @bp-subcard-bg; }
        // 选中项：文字黑色加粗 + 右侧对号
        &.active .name { color: @bp-title; font-weight: 600; }
        .name { flex: 1; }
        .hl { color: @bp-blue; }
        .check {
          width: 16px;
          height: 16px;
          margin-left: 8px;
          flex-shrink: 0;
        }
      }
      .empty {
        padding: 12px 10px;
        font-size: 12px;
        color: @bp-label;
        text-align: center;
      }
    }
  }
}
</style>
